// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import React, { useCallback, useMemo } from 'react';
import { NumberWheelProps, WheelPickerOption } from 'util/Types';
import { WheelPicker } from 'components/atoms/WheelPicker';
import styles from './NumberWheel.module.scss';
import { mergeClasses } from 'util/commons';

const NumberWheel: React.FC<NumberWheelProps> = ({
  value,
  min = 0,
  max = 99.9,
  decimalPlaces = 1,
  showDecimal = true,
  onChange,
  separator,
  classNames,
  wheelConfigs,
}) => {
  // Helper function to safely parse number parts from string
  const parseNumberParts = (num: number) => {
    const numStr = num.toString();
    const parts = numStr.split('.');
    const integerPart = parseInt(parts[0] || '0', 10);
    const decimalStr = (parts[1] || '0')
      .padEnd(decimalPlaces, '0')
      .substring(0, decimalPlaces);
    const decimalPart = parseInt(decimalStr, 10);
    return { integerPart, decimalPart };
  };

  // Calculate integer bounds from total min/max using string parsing
  const minParts = parseNumberParts(min);
  const maxParts = parseNumberParts(max);
  const integerMin = minParts.integerPart;
  const integerMax = maxParts.integerPart;

  // Parse current value
  const currentValue = value ?? 0;
  const { integerPart, decimalPart } = parseNumberParts(currentValue);

  // Generate options
  const integerOptions = useMemo(() => {
    const options: WheelPickerOption[] = [];
    for (let i = integerMin; i <= integerMax; i++) {
      options.push({
        value: i.toString(),
        label: i.toString(),
      });
    }
    return options;
  }, [integerMin, integerMax]);

  const decimalOptions = useMemo(() => {
    const options: WheelPickerOption[] = [];

    // Calculate decimal bounds based on current integer value
    let decimalMin = 0;
    let decimalMax = Math.pow(10, decimalPlaces) - 1;

    // If at minimum integer, start from minimum decimal
    if (integerPart === minParts.integerPart) {
      decimalMin = minParts.decimalPart;
    }

    // If at maximum integer, end at maximum decimal
    if (integerPart === maxParts.integerPart) {
      decimalMax = maxParts.decimalPart;
    }

    // Special case: if max is a whole number (e.g., 300.0) and we're at max integer,
    // only show one option: "00"
    if (integerPart === maxParts.integerPart && maxParts.decimalPart === 0) {
      options.push({
        value: '0',
        label: '0'.padStart(decimalPlaces, '0'),
      });
    } else {
      for (let i = decimalMin; i <= decimalMax; i++) {
        options.push({
          value: i.toString(),
          label: i.toString().padStart(decimalPlaces, '0'),
        });
      }
    }

    return options;
  }, [
    integerPart,
    minParts.integerPart,
    minParts.decimalPart,
    maxParts.integerPart,
    maxParts.decimalPart,
    decimalPlaces,
  ]);

  // Handle value changes
  const handleIntegerChange = useCallback(
    (integerValue: string) => {
      if (onChange) {
        const newInteger = parseInt(integerValue, 10);
        let newDecimalPart = decimalPart;

        // Adjust decimal part if at boundary values
        if (newInteger === minParts.integerPart) {
          newDecimalPart = Math.max(newDecimalPart, minParts.decimalPart);
        }

        if (newInteger === maxParts.integerPart) {
          newDecimalPart = Math.min(newDecimalPart, maxParts.decimalPart);
        }

        const newDecimal = newDecimalPart / Math.pow(10, decimalPlaces);
        const newValue = Math.min(Math.max(newInteger + newDecimal, min), max);
        onChange(newValue);
      }
    },
    [
      onChange,
      decimalPart,
      decimalPlaces,
      minParts.integerPart,
      minParts.decimalPart,
      maxParts.integerPart,
      maxParts.decimalPart,
    ]
  );

  const handleDecimalChange = useCallback(
    (decimalValue: string) => {
      if (onChange) {
        const newDecimal =
          parseInt(decimalValue, 10) / Math.pow(10, decimalPlaces);
        const newValue = Math.min(Math.max(integerPart + newDecimal, min), max);
        onChange(newValue);
      }
    },
    [onChange, integerPart, decimalPlaces, min, max]
  );

  return (
    <div className={mergeClasses(styles.numberWheel, classNames?.wrapper)}>
      <div
        className={mergeClasses(styles.wheelContainer, classNames?.container)}
      >
        <WheelPicker
          options={integerOptions}
          value={integerPart.toString()}
          onValueChange={handleIntegerChange}
          className={mergeClasses(
            styles.integerWheel,
            styles.roundedLeftHighlight,
            classNames?.integerWheel
          )}
          configs={wheelConfigs}
        />

        {showDecimal && (
          <>
            {separator && (
              <span
                className={mergeClasses(
                  styles.separator,
                  classNames?.separator
                )}
              >
                {separator}
              </span>
            )}
            <WheelPicker
              options={decimalOptions}
              value={decimalPart.toString()}
              onValueChange={handleDecimalChange}
              className={mergeClasses(
                styles.decimalWheel,
                styles.roundedRightHighlight,
                classNames?.decimalWheel,
                {
                  [styles.disabledScroll]: decimalOptions.length === 1,
                }
              )}
              infinite={decimalOptions.length > 1}
              configs={wheelConfigs}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default NumberWheel;
